import { useQuery } from '@apollo/client'
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, CircularProgress } from '@mui/material'

import { Button, Icons, Link, NotificationDialog, Stack, Text, Typography } from '../../../components'
import BankAccountSetupGuest from '../../../components/payment/zumrails-guest-iframe'
import * as Bank from '../../../components/payment/bank-transfer'
import { mutations, queries } from '../../../graphql'
import { calculateGiftAmount } from '../gift-details-math'

export const PaymentUI = forwardRef(({ user, setIsPaymentUILoading }, ref) => {
  const { refetch: refetchPublicLinkedZumRailsBanks } = useQuery(queries.paymentMethods.publicLinkedZumRailsBanks, {
    variables: { id: user?.id },
    fetchPolicy: 'network-only',
    skip: !user?.id,
  })

  useImperativeHandle(ref, () => ({
    getPaymentMethodId: async () => {
      if (!user?.id) {
        return null
      }
      const { data } = await refetchPublicLinkedZumRailsBanks()
      return Number(data?.publicLinkedZumRailsBanks?.[0]?.PaymentMethodId)
    },
  }))

  const [openManualOption, setManualOption] = useState(false)
  const [enableManualOption, setEnableManualOption] = useState(false)

  const { data: { guestHasBankAccount } = {}, loading: guestHasBankAccountLoading } = useQuery(
    queries.paymentMethods.guestHasBankAccount,
    {
      variables: { email: user?.email },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      skip: !user?.id,
    }
  )

  const { data: { getZumRailsUserGuest } = {}, refetch: refetchGetZumRailsUserGuest } = useQuery(
    queries.paymentMethods.getZumRailsUserGuest,
    {
      variables: { id: user?.id },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      skip: !user?.id,
    }
  )

  useEffect(() => {
    if (getZumRailsUserGuest && getZumRailsUserGuest !== '') {
      setEnableManualOption(getZumRailsUserGuest)
    } else {
      setEnableManualOption(false)
    }
  }, [getZumRailsUserGuest])

  useEffect(() => {
    if (guestHasBankAccountLoading || guestHasBankAccount === undefined) {
      setIsPaymentUILoading?.(true)
    } else {
      setIsPaymentUILoading?.(false)
    }
  }, [guestHasBankAccountLoading, guestHasBankAccount, setIsPaymentUILoading])

  const content = () => {
    if (guestHasBankAccountLoading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
          <CircularProgress color="info" />
          <div style={{ marginLeft: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Text.Body>One moment...</Text.Body>
          </div>
        </div>
      )
    }

    if (guestHasBankAccount === undefined) {
      return (
        <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
          <CircularProgress color="info" />
          <div style={{ marginLeft: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Text.Body>Loading bank account information...</Text.Body>
          </div>
        </div>
      )
    }

    if (guestHasBankAccount) {
      return <BankTransferGuestForm user={user} />
    }

    return (
      <>
        <BankAccountSetupGuest
          user={user}
          setEnableManualOption={setEnableManualOption}
          refetchGetZumRailsUserGuest={refetchGetZumRailsUserGuest}
          setIsPaymentUILoading={setIsPaymentUILoading}
          hideText
        />
        {enableManualOption && (
          <Stack>
            <Text.Body>
              We recognize that not all banks are available for aggregation. If you are experiencing issues connecting
              to your bank using your online banking credentials, you can manually enter your banking information.
              Please ensure that all details are entered accurately to avoid any delays or errors.
            </Text.Body>
            <Box sx={{ mb: 1 }}>
              <Button variant="contained" onClick={() => setManualOption(true)}>
                Manual Entry
              </Button>
            </Box>
          </Stack>
        )}
      </>
    )
  }

  return (
    <Stack spacing={2}>
      <Text.Body>
        To complete your donation, securely connect your bank account. This allows for a fast, direct transfer from your
        account to your Giving Wallet.
      </Text.Body>
      <Stack spacing={4}>
        {openManualOption ? (
          <Text.Body>
            Sorry, this is still a work in progress. Please contact{' '}
            <Link href="mailto:support@givewise.ca">support@givewise.ca</Link> for more information.
          </Text.Body>
        ) : (
          content()
        )}
        <Bank.FAQ />
      </Stack>
    </Stack>
  )
})

function BankTransferGuestForm({ user }) {
  const { data: { publicLinkedZumRailsBanks = [] } = {}, loading: loadingPublicLinkedZumRailsBanks } = useQuery(
    queries.paymentMethods.publicLinkedZumRailsBanks,
    {
      variables: { id: user?.id },
      fetchPolicy: 'network-only',
      skip: !user?.id,
    }
  )

  const { Institution, AccountNumberLast4 } = publicLinkedZumRailsBanks.length > 0 ? publicLinkedZumRailsBanks[0] : {}

  const bankAccountInfo = () => {
    const loaderOrInfo = () => {
      if (loadingPublicLinkedZumRailsBanks) {
        return (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginLeft: '8px',
            }}
          >
            <CircularProgress size={16} color="info" />
          </div>
        )
      }

      return (
        <Text.Body style={{ marginLeft: '8px', color: 'grey' }}>
          {`${Institution} - **** ${AccountNumberLast4}`}
        </Text.Body>
      )
    }

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-start',
          padding: '8px',
        }}
      >
        <Icons.Account />
        <Text.Body style={{ marginLeft: '8px' }}>Bank Account</Text.Body>
        {loaderOrInfo()}
      </div>
    )
  }

  return <>{bankAccountInfo()}</>
}

export async function getPaymentValues({ paymentMethodApi, values }) {
  const paymentMethodId = await paymentMethodApi.current.getPaymentMethodId()
  return {
    ...values,
    paymentMethodId,
  }
}

export function SuccessUI({ grantAmount, donationTargetName, donationTargetId }) {
  const navigate = useNavigate()

  const handleSignUpClick = () => {
    navigate('/sign-up')
  }

  return (
    <NotificationDialog
      open
      onClose={handleSignUpClick}
      type="success"
      title="Thank You for Your Generosity!"
      message={
        <>
          <Typography variant="body1" gutterBottom>
            Your donation of <strong>${grantAmount}</strong> to <strong>{donationTargetName}</strong> (
            <strong>{donationTargetId}</strong>) has been successfully processed. Together, we&apos;re making an impact,
            and we&apos;re so grateful for your support!
          </Typography>

          <Typography variant="h6" sx={{ mt: 2 }}>
            Here&apos;s what&apos;s next:
          </Typography>

          <Typography variant="body1" gutterBottom>
            To download your tax receipt and track your giving history, create a{' '}
            <Link to="/signup">GiveWise Giving Fund</Link>. It&apos;s quick, easy, and free!
          </Typography>

          <Typography variant="body1">With your GiveWise Giving Fund account, you can:</Typography>
          <ul>
            <li>
              <Typography variant="body1">Access all your tax receipts in one place.</Typography>
            </li>
            <li>
              <Typography variant="body1">Track your donations over time.</Typography>
            </li>
            <li>
              <Typography variant="body1">Set funds aside for future giving opportunities.</Typography>
            </li>
          </ul>
        </>
      }
      actions={[{ label: 'Create Your Giving Fund', onClick: handleSignUpClick }]}
    />
  )
}
